import React from 'react';
import {
  StyledCellSubValue,
  StyledCellHeader,
  StyledCellValue,
  StyledTableCell,
} from './styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { prepData } from './utils';
import { Column, Row } from './types';
import Label from './Label';
import { useTranslation } from 'react-i18next';
import { LcaDiagramSchema } from '../types';
import { ProductImpactOutSchema } from '../LCADiagram/Diagram/index.types';
import {
  getImpactAreaLabel,
  getLifecycleStageLabel,
} from 'src/Lca/LCADiagram/labels';

interface Props {
  diagramData?: LcaDiagramSchema;
  impactData?: ProductImpactOutSchema[];
  showSummaryRows?: boolean;
}

export default function ImpactGridTable({
  diagramData,
  impactData,
  showSummaryRows,
}: Readonly<Props>) {
  const { t } = useTranslation('lca', { keyPrefix: 'impactGrid' });
  const { t: tBase } = useTranslation('lca');
  const data = prepData(
    diagramData,
    impactData,
    (stageKey, fallbackLabel) =>
      getLifecycleStageLabel(tBase, stageKey, fallbackLabel),
    (impactAreaName, fallbackLabel) =>
      getImpactAreaLabel(tBase, impactAreaName, fallbackLabel)
  );

  const compact = !showSummaryRows;

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell
            sx={{ textAlign: 'center', padding: compact ? '2px' : '16px' }}
          >
            <StyledCellHeader compact={compact}>
              {t('impactCategory').toUpperCase()}
            </StyledCellHeader>
          </TableCell>
          {data.columns.map((col: { key: string; name: string }) => (
            <TableCell
              key={col.key}
              sx={{ textAlign: 'center', padding: compact ? '2px' : '16px' }}
            >
              <StyledCellHeader compact={compact}>{col.name}</StyledCellHeader>
            </TableCell>
          ))}
          <TableCell
            sx={{
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
              padding: compact ? '2px' : '16px',
            }}
          >
            <StyledCellHeader compact={compact}>
              {t('summary').toUpperCase()}
            </StyledCellHeader>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.rows.map((row: Row) => {
          return (
            <React.Fragment key={row.impactCategory.value}>
              {/* rendering two rows (value row, summary row underneath) */}
              <TableRow>
                <TableCell
                  rowSpan={showSummaryRows ? 2 : 1}
                  colSpan={1}
                  sx={{ padding: compact ? '2px' : '16px' }}
                >
                  <StyledCellValue sx={{ textAlign: 'left' }} compact={compact}>
                    {row.impactCategory.value}
                  </StyledCellValue>
                  <br />
                  <StyledCellSubValue compact={compact}>
                    {row.impactCategory.subvalue}
                  </StyledCellSubValue>
                </TableCell>
                {data.columns.map((col: Column) => {
                  const {
                    value,
                    rowSpan,
                    colSpan,
                    summary,
                    color = undefined,
                  } = row.columns[col.key] || {};
                  return (
                    <StyledTableCell
                      key={`${row.impactCategory}-${col.key}-summary`}
                      rowSpan={showSummaryRows ? rowSpan : 1}
                      colSpan={colSpan}
                      sx={showSummaryRows ? { borderBottom: 'none' } : {}}
                    >
                      <Label
                        compact={compact}
                        color={color}
                        max={summary || 0}
                        value={value || 0}
                      />
                    </StyledTableCell>
                  );
                })}
                <TableCell
                  sx={{ backgroundColor: '#f5f5f5' }}
                  rowSpan={showSummaryRows ? 2 : 1}
                  colSpan={1}
                >
                  <StyledCellValue compact={compact}>
                    <Label
                      compact={compact}
                      value={row.summary}
                      color={row.color}
                      max={row.summary}
                    />
                  </StyledCellValue>
                </TableCell>
              </TableRow>

              {/* rendering summary row */}
              {showSummaryRows && (
                <TableRow
                  sx={{ borderBottom: '1.25px solid rgb(224, 224, 224)' }}
                >
                  <StyledTableCell colSpan={3}>
                    <Label
                      color={row.color}
                      max={row.rawMaterialSummary}
                      value={row.rawMaterialSummary}
                    />
                  </StyledTableCell>
                  <StyledTableCell colSpan={2}>
                    <Label
                      color={row.color}
                      max={row.distributionSummary}
                      value={row.distributionSummary}
                    />
                  </StyledTableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
